/**
 * @fileoverview Dashboard stat / KPI card with trend indicator
 * @module ctl-stat-card
 * @version 1.0.0
 *
 * @description
 * A compact metric card designed for dashboards. Shows a headline value,
 * a label, an optional trend badge (up / down / neutral vs. previous period),
 * and an optional icon slot.
 *
 * @example
 * <ctl-stat-card label="Page Visits" value="12,430" trend="+8.4%" trend-direction="up"></ctl-stat-card>
 * <ctl-stat-card label="Revenue" value="$84,200" trend="-2.1%" trend-direction="down" loading></ctl-stat-card>
 */

import "../foundation/ctl-skeleton.js";

const template = document.createElement("template");
template.innerHTML = `
<style>
  :host {
    display: block;
    font-family: var(--ctl-font-sans, system-ui, -apple-system, Segoe UI, Roboto, Arial);
    color: var(--ctl-color-text);
  }

  .card {
    background: var(--ctl-color-surface);
    border: 1px solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-lg);
    box-shadow: var(--ctl-shadow-card);
    padding: var(--ctl-space-4) var(--ctl-space-4-5);
    display: flex;
    flex-direction: column;
    gap: var(--ctl-space-2-5);
    transition: box-shadow var(--ctl-transition-fast, 150ms);
  }

  .card:hover {
    box-shadow: var(--ctl-shadow-card-hover);
  }

  .header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--ctl-space-2);
  }

  .label {
    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-semibold);
    color: var(--ctl-color-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    line-height: 1.3;
  }

  .icon-slot {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: var(--ctl-radius-md);
    background: color-mix(in srgb, var(--ctl-color-brand) 12%, var(--ctl-color-surface));
    color: var(--ctl-color-brand);
    flex-shrink: 0;
  }

  .icon-slot:empty { display: none; }

  .value {
    font-size: var(--ctl-font-size-2xl-plus);
    font-weight: var(--ctl-font-weight-extrabold);
    letter-spacing: -0.02em;
    line-height: 1;
    color: var(--ctl-color-text);
  }

  .footer {
    display: flex;
    align-items: center;
    gap: var(--ctl-space-2);
    flex-wrap: wrap;
    min-height: 20px;
  }

  .trend {
    display: inline-flex;
    align-items: center;
    gap: var(--ctl-space-1);
    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-bold);
    padding: 2px var(--ctl-space-2);
    border-radius: var(--ctl-radius-full);
  }

  .trend[data-dir="up"] {
    background: color-mix(in srgb, var(--ctl-color-success) 14%, var(--ctl-color-surface));
    color: var(--ctl-color-success);
  }

  .trend[data-dir="down"] {
    background: color-mix(in srgb, var(--ctl-color-danger) 14%, var(--ctl-color-surface));
    color: var(--ctl-color-danger);
  }

  .trend[data-dir="neutral"] {
    background: color-mix(in srgb, var(--ctl-color-muted) 14%, var(--ctl-color-surface));
    color: var(--ctl-color-muted);
  }

  .trend-arrow { font-size: var(--ctl-font-size-2xs); }

  .subtext {
    font-size: var(--ctl-font-size-sm);
    color: var(--ctl-color-muted);
  }

  /* Loading skeleton */
  :host([loading]) .real { display: none; }
  .loading { display: none; flex-direction: column; gap: var(--ctl-space-2-5); }
  :host([loading]) .loading { display: flex; }
</style>

<div class="card" part="card">
  <div class="real">
    <div class="header">
      <div class="label" id="label"></div>
      <div class="icon-slot" part="icon"><slot name="icon"></slot></div>
    </div>
    <div class="value" id="value"></div>
    <div class="footer">
      <span class="trend" id="trend" hidden></span>
      <span class="subtext" id="subtext"></span>
    </div>
  </div>

  <div class="loading" part="loading" aria-hidden="true">
    <ctl-skeleton variant="rect" height="12px" width="50%"></ctl-skeleton>
    <ctl-skeleton variant="rect" height="28px" width="70%"></ctl-skeleton>
    <ctl-skeleton variant="rect" height="10px" width="40%"></ctl-skeleton>
  </div>
</div>
`;

export class CtlStatCard extends HTMLElement {
  static get observedAttributes() {
    return ["label", "value", "trend", "trend-direction", "subtext", "loading"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._labelEl = this.shadowRoot.getElementById("label");
    this._valueEl = this.shadowRoot.getElementById("value");
    this._trendEl = this.shadowRoot.getElementById("trend");
    this._subtextEl = this.shadowRoot.getElementById("subtext");
  }

  connectedCallback() { this._render(); }
  attributeChangedCallback() { this._render(); }

  get label() { return this.getAttribute("label") || ""; }
  set label(v) { this.setAttribute("label", String(v ?? "")); }

  get value() { return this.getAttribute("value") || ""; }
  set value(v) { this.setAttribute("value", String(v ?? "")); }

  get trend() { return this.getAttribute("trend") || ""; }
  set trend(v) { v == null ? this.removeAttribute("trend") : this.setAttribute("trend", String(v)); }

  get trendDirection() { return this.getAttribute("trend-direction") || "neutral"; }
  set trendDirection(v) { this.setAttribute("trend-direction", String(v ?? "neutral")); }

  get subtext() { return this.getAttribute("subtext") || ""; }
  set subtext(v) { this.setAttribute("subtext", String(v ?? "")); }

  get loading() { return this.hasAttribute("loading"); }
  set loading(v) { v ? this.setAttribute("loading", "") : this.removeAttribute("loading"); }

  _render() {
    this._labelEl.textContent = this.label;
    this._valueEl.textContent = this.value;
    this._subtextEl.textContent = this.subtext;

    const trend = this.trend;
    const dir = this.trendDirection;

    if (trend) {
      const arrows = { up: "▲", down: "▼", neutral: "–" };
      this._trendEl.hidden = false;
      this._trendEl.dataset.dir = dir;
      this._trendEl.innerHTML = `<span class="trend-arrow" aria-hidden="true">${arrows[dir] || "–"}</span>${this._esc(trend)}`;
      this._trendEl.setAttribute("aria-label", `Trend: ${trend} ${dir}`);
    } else {
      this._trendEl.hidden = true;
    }
  }

  _esc(s) {
    return String(s ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;");
  }
}

if (!customElements.get("ctl-stat-card")) {
  customElements.define("ctl-stat-card", CtlStatCard);
}
